import React, { useState, useEffect, useContext, useRef } from 'react'
import { Box, Text, TextArea, Button, Notification } from "grommet"
import { Star, Notes, AddCircle } from 'grommet-icons'
import { gql, useSubscription, useMutation } from '@apollo/client'
import { usePuserId } from '../../utils/puser'
import * as moment from "moment-timezone"

import UserContext from '../../context/UserContext'

const TTNotes = ({ topContent, middleContent, bottomContent, icon = null, width = "300px", backgroundColor = "light-2" }) => {
    return (
        <Box width={width} direction="column" gap="3px" background={backgroundColor} pad="xsmall" round="xsmall" margin={{ top: "5px" }}>
            <Box direction="row" gap="3px" align="center">{icon} <Text size="xsmall" color="brand"> {topContent}</Text></Box>
            <Box direction="row" gap="small" align="center">
                <Text size="small" weight="bold">{middleContent}</Text>
                <Text size="xsmall">{bottomContent}</Text>
            </Box>
        </Box>
    )
}

export const InstanceNotes = ({ instanceId }) => {
    const INSTANCE_NOTES = gql`
    subscription MyQuery($instanceId: String!) {
    notesInstance(where: {instanceId: {_eq: $instanceId}}) {
        notes
        puser {
        email
        }
        timestamp
    }
    }
    `
    const [notes, setNotes] = useState("")
    const [author, setAuthor] = useState("")
    const [timestamp, setTimestamp] = useState("")

    const instanceNotesData = useSubscription(INSTANCE_NOTES, { variables: { instanceId: instanceId } })

    useEffect(() => {
        if (!instanceNotesData.loading && instanceNotesData.data && instanceNotesData.data.notesInstance) {
            if (instanceNotesData.data.notesInstance.length > 0) {
                let snd = instanceNotesData.data.notesInstance[0]
                if (snd.notes.length > 0) {
                    setTimestamp(snd.timestamp)
                    setAuthor(snd.puser.email)
                    setNotes(snd.notes)
                } else {
                    setTimestamp("")
                    setAuthor("")
                    setNotes("")
                }
            } else {
                setTimestamp("")
                setAuthor("")
                setNotes("")
            }

        }
    }, [instanceNotesData.loading, instanceNotesData.data, instanceId])

    const token = useContext(UserContext)
    const [hasAdminScope, setHasAdminScope] = useState(false)
    useEffect(() => {
        if (token.scopes.indexOf("triage/ad") !== -1) {
            setHasAdminScope(true)
        }
    }, [token.scopes])

    if (hasAdminScope) {
        return <TTNotes
            width="300px"
            icon={<Notes size="16px" color="brand" />}
            backgroundColor={"ttpartial"}
            topContent={timestamp ? "Instance Notes (" + timestamp + ")" : "Instance Notes"}
            middleContent={""}
            bottomContent={
                <Box direction="column" gap="small">
                    <Text size="xsmall" weight="bold">{author}</Text>
                    <MultilineEditInstance value={notes} setValue={setNotes} instanceId={instanceId} />
                </Box>
            } />
    } else if (!hasAdminScope && notes.length > 0) {
        return <TTNotes
            width="300px"
            icon={<Notes size="16px" color="brand" />}
            backgroundColor={"ttpartial"}
            topContent={timestamp ? "Instance Notes (" + timestamp + ")" : "Instance Notes"}
            middleContent={""}
            bottomContent={
                <Box direction="column" gap="small">
                    <Text size="xsmall" weight="bold">{author}</Text>
                    <Text wordBreak="break-word" color="ttdown" size="xsmall" margin="none" textAlign="start" weight="bold">{notes}</Text>
                </Box>
            } />
    } else {
        return null
    }
}

export const NodeNotes = ({ nodeName }) => {
    const NODE_NODES = gql`
    subscription MyQuery($nodeName: String!) {
    notesNode(where: {nodeName: {_eq: $nodeName}}) {
        puser {
        email
        }
        notes
        timestamp
    }
    }
    `

    const [notes, setNotes] = useState("")
    const [author, setAuthor] = useState("")
    const [timestamp, setTimestamp] = useState("")

    const nodeNodesData = useSubscription(NODE_NODES, { variables: { nodeName: nodeName } })

    useEffect(() => {
        if (!nodeNodesData.loading && nodeNodesData.data && nodeNodesData.data.notesNode) {
            if (nodeNodesData.data.notesNode.length > 0) {
                let snd = nodeNodesData.data.notesNode[0]
                if (snd.notes.length > 0) {
                    setTimestamp(snd.timestamp)
                    setAuthor(snd.puser.email)
                    setNotes(snd.notes)
                } else {
                    setTimestamp("")
                    setAuthor("")
                    setNotes("")
                }
            } else {
                setTimestamp("")
                setAuthor("")
                setNotes("")
            }

        }
    }, [nodeNodesData.loading, nodeNodesData.data, nodeName])

    const token = useContext(UserContext)
    const [hasAdminScope, setHasAdminScope] = useState(false)
    useEffect(() => {
        if (token.scopes.indexOf("triage/ad") !== -1) {
            setHasAdminScope(true)
        }
    }, [token.scopes])

    if (hasAdminScope) {
        return <TTNotes
            width="300px"
            icon={<Star size="16px" color="brand" />}
            backgroundColor={"ttpartial"}
            topContent={timestamp ? "Node Notes (" + timestamp + ")" : "Node Notes"}
            middleContent={""}
            bottomContent={
                <Box direction="column" gap="small">
                    <Text size="xsmall" weight="bold">{author}</Text>
                    <MultilineEditNodeServer value={notes} setValue={setNotes} nodeName={nodeName} />
                </Box>
            } />
    } else if (!hasAdminScope && notes.length > 0) {
        return <TTNotes
            width="300px"
            icon={<Star size="16px" color="brand" />}
            backgroundColor={"ttpartial"}
            topContent={"Node Notes"}
            middleContent={""}
            bottomContent={
                <Box direction="column" gap="small">
                    <Text size="xsmall" weight="bold">{author}</Text>
                    <Text wordBreak="break-word" color="ttdown" size="xsmall" margin="none" textAlign="start" weight="bold">{notes}</Text>
                </Box>
            } />
    } else {
        return null
    }
}



const MultilineEditInstance = ({ value, setValue, instanceId }) => {

    const EDIT_NOTES_INSTANCE = gql`
    mutation MyMutation($object: notesInstance_insert_input!) {
    insert_notesInstance_one(on_conflict: {constraint: notesInstance_instanceId_unique, update_columns: [notes, timestamp, puserId]}, object: $object) {
        id
    }
    }
    `

    const [editingValue, setEditingValue] = useState(value);
    const [isEditing, setisEditing] = useState(false);

    const [notesUpdateUS] = useMutation(EDIT_NOTES_INSTANCE, { context: { backend: 'us' } })
    const [notesUpdateEU] = useMutation(EDIT_NOTES_INSTANCE, { context: { backend: 'eu' } })
    const token = useContext(UserContext)

    // we get the person.id for both regions as it is different we cannot assume
    const puser_id_us = usePuserId(token.personId, 'us')
    const puser_id_eu = usePuserId(token.personId, 'eu')

    const [hasAdminScope, setHasAdminScope] = useState(false)
    useEffect(() => {
        if (token.scopes.indexOf("triage/ad") !== -1) {
            setHasAdminScope(true)
        }
    }, [token.scopes])

    const onChange = (event) => {
        setEditingValue(event.target.value)
    };

    const onKeyDown = (event) => {
        if (event.key === "Enter" || event.key === "Escape") {
            event.target.blur();
        }
    };

    const onBlur = async (event) => {
        setisEditing(false)
        if (event.target.value.trim() === "") {
            setEditingValue(value);
            setValue(event.target.value.trim());
        } else {
            setValue(event.target.value);
        }

        const timestamp = moment(moment.utc()).toISOString()

        await notesUpdateUS({
            variables:
                { object: { "instanceId": instanceId, "notes": event.target.value, "puserId": puser_id_us, "timestamp": timestamp } }
        })

        await notesUpdateEU({
            variables:
                { object: { "instanceId": instanceId, "notes": event.target.value, "puserId": puser_id_eu, "timestamp": timestamp } }
        })

    };

    const textareaRef = useRef();

    return (
        <>

            {token && hasAdminScope && !isEditing &&
                <Box style={{ visibility: isEditing ? "hidden" : "visible" }}>
                    {value.length === 0 && <AddCircle size="16px" color="brand" onClick={() => { setisEditing(true) }} />}
                    <Text wordBreak="break-word" color="ttdown" size="xsmall" margin="none" textAlign="start" weight="bold" onClick={() => setisEditing(true)}>{value}</Text>
                </Box>
            }

            {token && hasAdminScope &&
                <Box style={{ visibility: isEditing ? "visible" : "hidden" }}>
                    <TextArea
                        fill={true}
                        value={editingValue}
                        onBlur={onBlur}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        size="xsmall"
                        ref={textareaRef}
                    />
                </Box>
            }

            {token && instanceId && hasAdminScope && value.length > 0 &&
                <DeleteNoteButton instanceId={instanceId} nodeName={null} />
            }

        </>
    );
};

const MultilineEditNodeServer = ({ value, setValue, nodeName }) => {
    const EDIT_NOTES_NODE = gql`
    mutation MyMutation($object: notesNode_insert_input!) {
    insert_notesNode_one(object: $object, on_conflict: {constraint: notesNode_nodeName_unique, update_columns: [timestamp, notes, puserId]}) {
        id
    }
    }
    `

    const [editingValue, setEditingValue] = useState(value);
    const [isEditing, setisEditing] = useState(false);

    // Required to add Mutation
    const [notesUpdateUS] = useMutation(EDIT_NOTES_NODE, { context: { backend: 'us' } })
    const [notesUpdateEU] = useMutation(EDIT_NOTES_NODE, { context: { backend: 'eu' } })
    const token = useContext(UserContext)

    // we get the person.id for both regions as it is different we cannot assume
    const puser_id_us = usePuserId(token.personId, 'us')
    const puser_id_eu = usePuserId(token.personId, 'eu')

    const [hasAdminScope, setHasAdminScope] = useState(false)
    useEffect(() => {
        if (token.scopes.indexOf("triage/ad") !== -1) {
            setHasAdminScope(true)
        }
    }, [token.scopes])

    const onChange = (event) => {
        setEditingValue(event.target.value)
    };

    const onKeyDown = (event) => {
        if (event.key === "Enter" || event.key === "Escape") {
            event.target.blur();
        }
    };

    const onBlur = async (event) => {
        setisEditing(false)
        if (event.target.value.trim() === "") {
            setEditingValue(value);
            setValue(event.target.value.trim());
        } else {
            setValue(event.target.value);
        }

        const timestamp = moment(moment.utc()).toISOString()

        await notesUpdateUS({
            variables:
                { "object": { "nodeName": nodeName, "notes": event.target.value, "puserId": puser_id_us, "timestamp": timestamp } }
        })
        await notesUpdateEU({
            variables:
                { "object": { "nodeName": nodeName, "notes": event.target.value, "puserId": puser_id_eu, "timestamp": timestamp } }
        })

    };

    const textareaRef = useRef();

    return (
        <>
            {token && nodeName && hasAdminScope && !isEditing &&
                <Box style={{ visibility: isEditing ? "hidden" : "visible" }}>
                    {value.length === 0 && <AddCircle size="16px" color="brand" onClick={() => { setisEditing(true) }} />}
                    <Text wordBreak="break-word" color="ttdown" size="xsmall" margin="none" textAlign="start" weight="bold" onClick={() => setisEditing(true)}>{value}</Text>
                </Box>
            }

            {token && nodeName && hasAdminScope &&
                <Box style={{ visibility: isEditing ? "visible" : "hidden" }}>
                    <TextArea
                        fill={true}
                        value={editingValue}
                        onBlur={onBlur}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        on
                        size="xsmall"
                        ref={textareaRef}
                    />
                </Box>
            }

            {token && nodeName && hasAdminScope && value.length > 0 &&
                <DeleteNoteButton instanceId={null} nodeName={nodeName} />
            }

        </>
    );
};

const DeleteNoteButton = ({ instanceId = null, nodeName = null }) => {

    const [visible, setVisible] = React.useState();
    const DELETE_NOTES_NODE = gql`
    mutation delete_notesNode($variable: String!) {
    delete_notesNode(where: {nodeName: {_eq: $variable}}) {
        affected_rows
    }
    }
    `
    const DELETE_NOTES_INSTANCEID = gql`
    mutation delete_notesNode($variable: String!) {
    delete_notesInstance(where: {instanceId: {_eq: $variable}}) {
        affected_rows
    }
    }
    `
    const [notesUpdateUS] = useMutation(nodeName ? DELETE_NOTES_NODE : DELETE_NOTES_INSTANCEID, { context: { backend: 'us' } })
    const [notesUpdateEU] = useMutation(nodeName ? DELETE_NOTES_NODE : DELETE_NOTES_INSTANCEID, { context: { backend: 'eu' } })
    const clickDelete = async () => {
        setVisible(true);
        await notesUpdateUS({
            variables:
                { "variable": nodeName ? nodeName : instanceId }
        })
        await notesUpdateEU({
            variables:
                { "variable": nodeName ? nodeName : instanceId }
        })
    };

    return (
        <>
            <Box
                justify="center"
                align="center"
                gap="none"
                width="75px"
                background="ttdown"
                alignContent="center"
                round="xsmall"
                onClick={() => clickDelete()}
            >
                <Button plain label={<Text color="white" size="12px" weight="normal">Delete</Text>} margin={{ top: "xxsmall" }} hoverIndicator={{ color: "light-1", opacity: "weak" }} />
            </Box>
            {visible && (
                <Notification
                    toast
                    title="Note Deleted"
                    status="normal"
                    message="The note has been successfully deleted."
                    onClose={() => setVisible(false)}
                />
            )}
        </>
    )
}
